import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Pencil,
  Ban,
  BellRing,
  CalendarClock,
  DoorOpen,
  CheckCircle,
  XCircle,
  History,
  ChevronDown,
  ChevronUp,
  Filter,
} from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { Button } from "@/shared/components/Button";
import { Paginacao } from "@/shared/components/Paginacao";
import { useAuth } from "@/core/hooks/useAuth";
import { useGestaoModulo } from "@/core/hooks/useGestaoModulo";
import { useMinisteriosQueLidero } from "@/core/hooks/useMinisterios";
import { rotuloDiaLongo, formatarHora, chaveDia } from "@/shared/lib/datas";
import { FormularioReserva } from "@/modules/reservas/components/FormularioReserva";
import { useAmbientesAtivos } from "@/modules/reservas/hooks/useAmbientes";
import { useMinisterioOpcoes } from "@/modules/reservas/hooks/useMinisterioOpcoes";
import {
  useReservas,
  useCriarReserva,
  useAtualizarReserva,
  useCancelarReserva,
  useAprovarReserva,
  useRejeitarReserva,
} from "@/modules/reservas/hooks/useReservas";
import type { DadosReserva, ReservaComRelacoes } from "@/modules/reservas/services/reservasService";

/** Rótulos e cores do badge de status — mesmo padrão de CORES_STATUS em empréstimos. */
const STATUS_LABEL_RESERVA: Record<string, string> = {
  pendente: "Pendente",
  confirmada: "Confirmada",
  cancelada: "Cancelada",
  recusada: "Recusada",
};
const CORES_STATUS_RESERVA: Record<string, string> = {
  pendente: "bg-warning/10 text-warning",
  confirmada: "bg-success/10 text-success",
  cancelada: "bg-surface-2 text-muted",
  recusada: "bg-danger/12 text-danger",
};
const TAM_PAGINA_HIST = 10;

// Reteste 22-23/07/2026: uma reserva vai para o HISTÓRICO se foi cancelada/
// recusada OU já passou (fim < agora). Cancelada (pelo dono) e Recusada (pelo
// responsável, quando pendente) ganham rótulos próprios.
function ehHistoricoReserva(r: ReservaComRelacoes, agora: Date): boolean {
  return r.status === "cancelada" || r.status === "recusada" || new Date(r.fim) < agora;
}
function rotuloStatusReserva(r: ReservaComRelacoes, agora: Date): { label: string; cls: string } {
  const passado = new Date(r.fim) < agora;
  if (r.status === "cancelada") return { label: "Cancelada", cls: "bg-surface-2 text-muted" };
  if (r.status === "recusada") return { label: "Recusada", cls: "bg-danger/12 text-danger" };
  if (r.status === "pendente")
    return passado
      ? { label: "Expirada", cls: "bg-surface-2 text-muted" }
      : { label: "Pendente", cls: "bg-warning/10 text-warning" };
  return passado
    ? { label: "Realizada", cls: "bg-primary/10 text-primary" }
    : { label: "Confirmada", cls: "bg-success/10 text-success" };
}

export function GerenciarReservas() {
  const navigate = useNavigate();
  const { pessoa, podeGerenciarReservas, podeGerenciarEventos } = useAuth();
  const { data: reservas, isLoading } = useReservas();
  // BUG-05 + reteste 22-23/07/2026: a tela separa "ativas" (futuras: pendentes/
  // confirmadas) do "histórico" (passadas, canceladas e recusadas).
  const agora = useMemo(() => new Date(), []);
  const reservasAtivas = reservas?.filter((r) => !ehHistoricoReserva(r, agora)) ?? [];
  const reservasHistorico = reservas?.filter((r) => ehHistoricoReserva(r, agora)) ?? [];
  const { data: ambientes } = useAmbientesAtivos();
  const { data: ministeriosLiderados } = useMinisteriosQueLidero();
  // F6-14 (revisão 21/07/2026): em modo "individual" (só importa com 2+
  // ministérios responsáveis), gerenciar uma reserva fica restrito ao(s)
  // ministério(s) pelo(s) qual(is) a pessoa gerencia o módulo — a RLS já
  // aplica esse recorte (migration 0048) tanto para o módulo Reservas
  // quanto para o Eventos (reserva vinculada a evento).
  const { modoDados: modoDadosReservas, ministeriosGestao: ministeriosGestaoReservas } =
    useGestaoModulo("reservas");
  const { modoDados: modoDadosEventosR, ministeriosGestao: ministeriosGestaoEventosR } =
    useGestaoModulo("eventos");
  // F2-32: quem gerencia o módulo Reservas (ou Eventos) escolhe entre TODOS
  // os ministérios (ou só os que gerencia, em modo individual); os demais só
  // entre os que lideram.
  const ministerioOpcoes = useMinisterioOpcoes("reservas", podeGerenciarReservas);
  const criar = useCriarReserva();
  const atualizar = useAtualizarReserva();
  const cancelar = useCancelarReserva();
  const aprovar = useAprovarReserva();
  const rejeitar = useRejeitarReserva();

  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<ReservaComRelacoes | null>(null);
  const [erroForm, setErroForm] = useState<string | null>(null);
  const [paraCancelar, setParaCancelar] = useState<ReservaComRelacoes | null>(null);
  const [erroCancelamento, setErroCancelamento] = useState<string | null>(null);
  // C-03 (reteste 23-24/07/2026): motivo obrigatório no cancelamento, mesma
  // paridade com o cancelamento de Eventos — antes cancelava sem pedir nada.
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [paraRejeitar, setParaRejeitar] = useState<ReservaComRelacoes | null>(null);
  // Motivo obrigatório na recusa, mesma paridade com a recusa de Eventos.
  const [motivoRejeicao, setMotivoRejeicao] = useState("");
  // F4-14 (revisão 20/07/2026): aprovar confirmava a reserva na hora do
  // clique, sem chance de desistir — agora passa por modal, igual rejeitar.
  const [paraAprovar, setParaAprovar] = useState<ReservaComRelacoes | null>(null);
  const [erroAprovacao, setErroAprovacao] = useState<string | null>(null);
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);
  const [mensagemInfo, setMensagemInfo] = useState<string | null>(null);
  // BUG-05: reservas canceladas/rejeitadas não somem mais — ficam num histórico
  // colapsado por padrão (não polui a lista de reservas ativas), mostrando o
  // motivo registrado em `observacoes` na rejeição.
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [paginaHist, setPaginaHist] = useState(1);

  useEffect(() => {
    if (!mensagemSucesso) return;
    const t = setTimeout(() => setMensagemSucesso(null), 6000);
    return () => clearTimeout(t);
  }, [mensagemSucesso]);

  // F5-09/F6-05/F6-06: filtros de listagem — por ministério responsável, dia e
  // período do horário. Nenhum filtro selecionado = mostra tudo (comportamento
  // anterior preservado).
  // UX-04 (auditoria 21/07/2026): filtros ficam refletidos na URL — atualizar a
  // página ou compartilhar o link preserva o que estava filtrado.
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroMinisterio = searchParams.get("ministerio") ?? "";
  const filtroDia = searchParams.get("dia") ?? "";
  const filtroPeriodo = (searchParams.get("periodo") ?? "") as "" | "manha" | "tarde" | "noite";

  function setFiltro(chave: "ministerio" | "dia" | "periodo", valor: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (valor) next.set(chave, valor);
        else next.delete(chave);
        return next;
      },
      { replace: true },
    );
  }

  const ministeriosNaLista = useMemo(() => {
    const nomes = new Map<string, string>();
    for (const r of reservas ?? []) {
      for (const m of r.ministerios) nomes.set(m.id, m.nome);
    }
    return Array.from(nomes.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [reservas]);

  function periodoDoHorario(iso: string): "manha" | "tarde" | "noite" {
    const hora = new Date(iso).getHours();
    if (hora < 12) return "manha";
    if (hora < 18) return "tarde";
    return "noite";
  }

  function passaNosFiltros(r: ReservaComRelacoes): boolean {
    if (filtroMinisterio && !r.ministerios.some((m) => m.id === filtroMinisterio)) return false;
    if (filtroDia && chaveDia(r.inicio) !== filtroDia) return false;
    if (filtroPeriodo && periodoDoHorario(r.inicio) !== filtroPeriodo) return false;
    return true;
  }

  const reservasAtivasFiltradas = useMemo(
    () => reservasAtivas.filter(passaNosFiltros).sort((a, b) => a.inicio.localeCompare(b.inicio)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reservasAtivas, filtroMinisterio, filtroDia, filtroPeriodo],
  );
  const reservasHistoricoFiltradas = useMemo(
    () => reservasHistorico.filter(passaNosFiltros).sort((a, b) => b.inicio.localeCompare(a.inicio)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reservasHistorico, filtroMinisterio, filtroDia, filtroPeriodo],
  );
  const totalPaginasHist = Math.max(1, Math.ceil(reservasHistoricoFiltradas.length / TAM_PAGINA_HIST));
  const paginaHistOk = Math.min(paginaHist, totalPaginasHist);
  const historicoPagina = reservasHistoricoFiltradas.slice(
    (paginaHistOk - 1) * TAM_PAGINA_HIST,
    paginaHistOk * TAM_PAGINA_HIST,
  );

  const filtrosAtivos = !!filtroMinisterio || !!filtroDia || !!filtroPeriodo;
  function limparFiltros() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("ministerio");
      next.delete("dia");
      next.delete("periodo");
      return next;
    }, { replace: true });
  }

  // Sprint 3 (22/07/2026): abrir edição/cancelamento vindo do Calendário
  // Central (?editar=<id> / ?cancelar=<id>). Reaproveita o modal/permissão
  // desta tela — a checagem podeGerirReserva continua sendo o portão.
  useEffect(() => {
    const editarId = searchParams.get("editar");
    const cancelarId = searchParams.get("cancelar");
    if ((!editarId && !cancelarId) || !reservas) return;
    const alvo = reservas.find((r) => r.id === (editarId ?? cancelarId));
    if (alvo && podeGerirReserva(alvo)) {
      if (editarId) abrirEdicao(alvo);
      else setParaCancelar(alvo);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("editar");
        next.delete("cancelar");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservas]);

  // Ambientes é configuração do módulo como um todo (não é de um ministério
  // específico) — Admin continua vendo esse botão normalmente.
  // F6-04: Ambientes é gestão do módulo Reservas — só quem gerencia Reservas.
  const podeVerAmbientes = podeGerenciarReservas;
  // Qualquer pessoa autenticada pode solicitar uma reserva — quem não administra o
  // módulo tem a solicitação criada como 'pendente' automaticamente (trigger no
  // banco, migration 0029), aguardando aprovação de um líder/gestor da Diaconia
  // (ou de quem quer que administre o módulo Reservas).
  const podeCriar = !!pessoa;
  // UX-01 (auditoria 20/07/2026): Admin só ganha botão de editar/cancelar numa
  // reserva de OUTRO ministério se ele mesmo gerencia o módulo — reserva sem
  // ministério (Sede) ou feita por ele mesmo continua no domínio dele. RLS
  // continua sendo o portão real, isso é só a interface não ficar poluída com
  // ações que não são "dele" de fato (F6-04 da auditoria).
  // Estrutura revisada (doc "Matriz de Perfis", 22/07/2026): aprovar/rejeitar
  // reserva é atribuição de quem gerencia o módulo Reservas (líder/gestor do
  // ministério responsável) — mesma regra do evento. O líder do ministério
  // dono continua podendo criar/cancelar a própria reserva (podeGerirReserva),
  // mas não aprovar. Antes (F4-12) o dono também aprovava; foi revogado.
  function podeAprovarReserva(_r: ReservaComRelacoes): boolean {
    return podeGerenciarReservas;
  }

  // F6-04: sem atalho de admin — editar/cancelar segue a mesma regra de CRUD
  // (gestor do módulo ou líder do ministério da reserva).
  function podeGerirReserva(r: ReservaComRelacoes): boolean {
    const ministeriosDaReserva = [r.ministerio_id, ...r.ministerios.map((m) => m.id)].filter(
      (id): id is string => !!id,
    );
    // F6-14 (revisão 21/07/2026): gestor do módulo Reservas — em modo
    // individual, só gerencia reservas do(s) ministério(s) pelo(s) qual(is)
    // gerencia o módulo, não de qualquer ministério responsável.
    if (podeGerenciarReservas) {
      if (modoDadosReservas !== "individual") return true;
      if (ministeriosDaReserva.some((id) => ministeriosGestaoReservas.includes(id))) return true;
    }
    // Idem para quem gerencia Eventos — reserva pode ter sido criada
    // automaticamente por um evento (lacuna pré-existente: a RLS já
    // permitia isso, a interface nunca checava).
    if (podeGerenciarEventos) {
      if (modoDadosEventosR !== "individual") return true;
      if (ministeriosDaReserva.some((id) => ministeriosGestaoEventosR.includes(id))) return true;
    }
    // Líder do(s) ministério(s) da reserva também gerencia (RLS já
    // permitia via eh_lider_ministerio, interface nunca checava).
    if (ministeriosDaReserva.some((id) => (ministeriosLiderados ?? []).some((m) => m.id === id))) {
      return true;
    }
    return false;
  }

  function abrirCriacao() {
    setEmEdicao(null);
    setErroForm(null);
    setModalAberto(true);
  }
  function abrirEdicao(r: ReservaComRelacoes) {
    // F4-07 (reteste): reserva criada por um evento não se edita direto — ela
    // acompanha o evento. Em vez de um erro genérico, orienta o usuário.
    if (r.evento_id) {
      setMensagemInfo(
        "Esta reserva foi criada por um evento. Para mudar horário, ambiente ou outras informações, edite o evento correspondente.",
      );
      return;
    }
    setMensagemInfo(null);
    setEmEdicao(r);
    setErroForm(null);
    setModalAberto(true);
  }

  async function handleSubmit(dados: DadosReserva) {
    setErroForm(null);
    try {
      if (emEdicao) {
        await atualizar.mutateAsync({ id: emEdicao.id, dados });
        setMensagemSucesso("Reserva atualizada!");
      } else {
        const resultado = await criar.mutateAsync({ ...dados, solicitadaPorPessoaId: pessoa?.id ?? null });
        setMensagemSucesso(
          resultado.status === "confirmada"
            ? "Reserva confirmada!"
            : "Solicitação enviada — aguardando aprovação de quem gerencia Reservas.",
        );
      }
      setModalAberto(false);
    } catch (e) {
      setErroForm(e instanceof Error ? e.message : "Não foi possível salvar a reserva.");
    }
  }

  async function confirmarCancelamento() {
    if (!paraCancelar) return;
    setErroCancelamento(null);
    try {
      await cancelar.mutateAsync({ id: paraCancelar.id, motivo: motivoCancelamento.trim() });
      setMensagemSucesso("Reserva cancelada.");
      setParaCancelar(null);
      setMotivoCancelamento("");
    } catch (e) {
      // Mantém o modal aberto mostrando o erro em vez de fechar como se tivesse
      // dado certo (mesmo problema relatado para exclusão de evento).
      setErroCancelamento(e instanceof Error ? e.message : "Não foi possível cancelar a reserva.");
    }
  }

  async function confirmarAprovacao() {
    if (!paraAprovar) return;
    setErroAprovacao(null);
    try {
      await aprovar.mutateAsync(paraAprovar.id);
      setMensagemSucesso(`Reserva "${paraAprovar.titulo}" aprovada!`);
      setParaAprovar(null);
    } catch (e) {
      setErroAprovacao(e instanceof Error ? e.message : "Não foi possível aprovar a reserva.");
      setParaAprovar(null);
    }
  }

  async function confirmarRejeicao() {
    if (!paraRejeitar) return;
    setErroAprovacao(null);
    try {
      await rejeitar.mutateAsync({ id: paraRejeitar.id, motivo: motivoRejeicao.trim() });
      setMensagemSucesso("Solicitação recusada — quem pediu foi avisado.");
      setParaRejeitar(null);
      setMotivoRejeicao("");
    } catch (e) {
      setErroAprovacao(e instanceof Error ? e.message : "Não foi possível recusar a reserva.");
    }
  }

  return (
    <>
      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-text">Reservas</h1>
          <div className="flex gap-2">
            {podeVerAmbientes && (
              <button
                onClick={() => navigate("/reservas/ambientes")}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-semibold text-text transition-colors hover:bg-surface-2"
              >
                <DoorOpen size={16} /> Ambientes
              </button>
            )}
            {podeCriar && <Button onClick={abrirCriacao}>+ Nova reserva</Button>}
          </div>
        </div>
        <p className="mt-1 text-sm text-muted">Reserva de salas e ambientes, com controle de conflitos.</p>

        {mensagemSucesso && (
          <p role="status" className="mt-4 rounded-md bg-success/10 px-4 py-3 text-sm text-success">
            {mensagemSucesso}
          </p>
        )}
        {mensagemInfo && (
          <p role="status" className="mt-4 rounded-md bg-info/10 px-4 py-3 text-sm text-info">
            {mensagemInfo}
          </p>
        )}
        {erroAprovacao && (
          <p role="alert" className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
            {erroAprovacao}
          </p>
        )}

        {!!reservas?.length && (
          <div className="mt-4 flex flex-col gap-2 rounded-xl border border-border bg-surface p-3 sm:flex-row sm:flex-wrap sm:items-center">
            <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-muted">
              <Filter size={14} /> Filtrar
            </span>
            <select
              value={filtroMinisterio}
              onChange={(e) => setFiltro("ministerio", e.target.value)}
              className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-text"
            >
              <option value="">Todos os ministérios</option>
              {ministeriosNaLista.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={filtroDia}
              onChange={(e) => setFiltro("dia", e.target.value)}
              aria-label="Filtrar por dia"
              className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-text"
            />
            <select
              value={filtroPeriodo}
              onChange={(e) => setFiltro("periodo", e.target.value)}
              className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-text"
            >
              <option value="">Qualquer horário</option>
              <option value="manha">Manhã</option>
              <option value="tarde">Tarde</option>
              <option value="noite">Noite</option>
            </select>
            {filtrosAtivos && (
              <button
                onClick={limparFiltros}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </div>
        )}

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
          {isLoading ? (
            <p className="p-6 text-sm text-muted">Carregando...</p>
          ) : !reservasAtivas.length ? (
            <p className="p-6 text-sm text-muted">Nenhuma reserva ativa no momento.</p>
          ) : !reservasAtivasFiltradas.length ? (
            <p className="p-6 text-sm text-muted">Nenhuma reserva encontrada com esses filtros.</p>
          ) : (
            <ul>
              {reservasAtivasFiltradas.map((r, idx) => (
                <li
                  key={r.id}
                  className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-warning/10 text-warning">
                    <CalendarClock size={20} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-text">{r.titulo}</p>
                    <p className="mt-0.5 truncate text-xs text-muted">
                      {r.ambiente_nome ?? "Ambiente"} · {rotuloDiaLongo(r.inicio)} ·{" "}
                      {formatarHora(r.inicio)}–{formatarHora(r.fim)} ·{" "}
                      {r.ministerios.length ? r.ministerios.map((m) => m.nome).join(", ") : "Administração"}
                      {" · "}
                      <span
                        className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                          CORES_STATUS_RESERVA[r.status] ?? "bg-surface-2 text-muted"
                        }`}
                      >
                        {STATUS_LABEL_RESERVA[r.status] ?? r.status}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {r.status === "pendente" && podeAprovarReserva(r) && (
                      <>
                        <button
                          onClick={() => setParaAprovar(r)}
                          aria-label="Aprovar reserva"
                          disabled={aprovar.isPending}
                          className="flex h-11 w-11 items-center justify-center rounded-lg text-success transition-colors hover:bg-success/10 disabled:opacity-50"
                        >
                          <CheckCircle size={18} />
                        </button>
                        <button
                          onClick={() => setParaRejeitar(r)}
                          aria-label="Recusar reserva"
                          className="flex h-11 w-11 items-center justify-center rounded-lg text-danger transition-colors hover:bg-danger/10"
                        >
                          <XCircle size={18} />
                        </button>
                      </>
                    )}
                    {podeGerirReserva(r) && (
                      <>
                        <button
                          onClick={() => abrirEdicao(r)}
                          aria-label="Editar"
                          className="flex h-11 w-11 items-center justify-center rounded-lg text-info transition-colors hover:bg-info/10"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setParaCancelar(r)}
                          aria-label="Cancelar reserva"
                          className="flex h-11 w-11 items-center justify-center rounded-lg text-danger transition-colors hover:bg-danger/10"
                        >
                          <Ban size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {reservasHistoricoFiltradas.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
            <button
              onClick={() => setHistoricoAberto((v) => !v)}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-text"
            >
              <span className="flex items-center gap-2">
                <History size={16} className="text-muted" />
                Histórico — passadas, canceladas e recusadas ({reservasHistoricoFiltradas.length})
              </span>
              {historicoAberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {historicoAberto && (
              <>
                <ul className="border-t border-border">
                  {historicoPagina.map((r, idx) => {
                    const st = rotuloStatusReserva(r, agora);
                    return (
                      <li
                        key={r.id}
                        className={`flex items-start gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}
                      >
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-surface-2 text-muted">
                          <CalendarClock size={20} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-text">{r.titulo}</p>
                          <p className="mt-0.5 truncate text-xs text-muted">
                            {r.ambiente_nome ?? "Ambiente"} · {rotuloDiaLongo(r.inicio)} ·{" "}
                            {formatarHora(r.inicio)}–{formatarHora(r.fim)} ·{" "}
                            {r.ministerios.length ? r.ministerios.map((m) => m.nome).join(", ") : "Administração"}
                            {" · "}
                            <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${st.cls}`}>
                              {st.label}
                            </span>
                          </p>
                          {r.observacoes && (
                            <p className="mt-1 text-xs text-muted">
                              <span className="font-medium text-text">Motivo:</span> {r.observacoes}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <Paginacao
                  paginaAtual={paginaHistOk}
                  totalPaginas={totalPaginasHist}
                  onMudarPagina={setPaginaHist}
                />
              </>
            )}
          </div>
        )}
      </main>

      <Modal
        titulo={emEdicao ? "Editar reserva" : "Nova reserva"}
        aberto={modalAberto}
        onClose={() => setModalAberto(false)}
        carregando={criar.isPending || atualizar.isPending}
      >
        <FormularioReserva
          valoresIniciais={emEdicao}
          ambientes={ambientes ?? []}
          ministerios={ministerioOpcoes}
          carregando={criar.isPending || atualizar.isPending}
          erro={erroForm}
          onSubmit={handleSubmit}
        />
      </Modal>

      <ModalConfirmacao
        aberto={!!paraCancelar}
        titulo="Cancelar reserva"
        mensagem={`Cancelar a reserva "${paraCancelar?.titulo}"? O horário do ambiente ficará livre.`}
        textoBotaoConfirmar="Cancelar reserva"
        variante="perigo"
        carregando={cancelar.isPending}
        erro={erroCancelamento}
        confirmarDesabilitado={!motivoCancelamento.trim()}
        onConfirmar={confirmarCancelamento}
        onCancelar={() => {
          setParaCancelar(null);
          setErroCancelamento(null);
          setMotivoCancelamento("");
        }}
      >
        <label htmlFor="motivo-cancelamento-reserva" className="mb-1.5 block text-xs font-semibold text-text">
          Motivo do cancelamento <span className="text-danger">*</span>
        </label>
        <textarea
          id="motivo-cancelamento-reserva"
          value={motivoCancelamento}
          onChange={(e) => setMotivoCancelamento(e.target.value)}
          rows={3}
          placeholder="Ex.: ambiente não vai mais ser necessário"
          className="w-full resize-none rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
        />
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-muted">
          <BellRing size={13} className="mt-px shrink-0 text-primary" />
          {paraCancelar?.ministerios.length
            ? `Quem solicitou e os líderes de ${paraCancelar.ministerios.map((m) => m.nome).join(", ")} vão receber este motivo na notificação.`
            : "Quem solicitou vai receber este motivo na notificação."}
        </p>
      </ModalConfirmacao>

      <ModalConfirmacao
        aberto={!!paraAprovar}
        titulo="Aprovar reserva"
        mensagem={`Aprovar a reserva "${paraAprovar?.titulo}"? O ambiente fica confirmado para esse horário.`}
        textoBotaoConfirmar="Aprovar"
        variante="padrao"
        carregando={aprovar.isPending}
        erro={erroAprovacao}
        onConfirmar={confirmarAprovacao}
        onCancelar={() => {
          setParaAprovar(null);
          setErroAprovacao(null);
        }}
      />

      <ModalConfirmacao
        aberto={!!paraRejeitar}
        titulo="Recusar solicitação"
        mensagem={`Recusar a solicitação de reserva "${paraRejeitar?.titulo}"? Quem solicitou será avisado.`}
        textoBotaoConfirmar="Recusar"
        variante="perigo"
        carregando={rejeitar.isPending}
        erro={erroAprovacao}
        confirmarDesabilitado={!motivoRejeicao.trim()}
        onConfirmar={confirmarRejeicao}
        onCancelar={() => {
          setParaRejeitar(null);
          setErroAprovacao(null);
          setMotivoRejeicao("");
        }}
      >
        <label htmlFor="motivo-rejeicao-reserva" className="mb-1.5 block text-xs font-semibold text-text">
          Motivo da recusa <span className="text-danger">*</span>
        </label>
        <textarea
          id="motivo-rejeicao-reserva"
          value={motivoRejeicao}
          onChange={(e) => setMotivoRejeicao(e.target.value)}
          rows={3}
          placeholder="Ex.: o ambiente já está reservado nesse horário"
          className="w-full resize-none rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
        />
        <p className="mt-2 text-[11px] leading-snug text-muted">
          O motivo fica registrado na reserva e aparece no histórico.
        </p>
      </ModalConfirmacao>
    </>
  );
}
